import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { Plus, Search, Trash2, Users } from "lucide-react";
import { format } from "date-fns";
import SopEditorDialog from "@/components/sops/sop-editor-dialog";

type SOP = {
  id: string;
  title: string;
  category: string | null;
  audience: "manus" | "humans" | "both";
  status: "draft" | "published";
  visibility?: "all" | "selected";
  shareCount?: number;
  sharedWith?: { id: string; name: string }[];
  createdAt: string;
  updatedAt: string;
};

/** Badge showing whether an SOP is visible to all assistants or a selected few. */
function SharingBadge({ sop }: { sop: SOP }) {
  // Sharing only applies when human assistants are part of the audience.
  if (sop.audience !== "humans" && sop.audience !== "both") return null;
  if (!sop.visibility || sop.visibility === "all") {
    return (
      <Badge variant="secondary" className="shrink-0" data-testid={`badge-sop-sharing-${sop.id}`}>
        <Users className="w-3 h-3 mr-1" />
        All assistants
      </Badge>
    );
  }
  const count = sop.shareCount ?? sop.sharedWith?.length ?? 0;
  const names = (sop.sharedWith ?? []).map((a) => a.name);
  const badge = (
    <Badge variant="outline" className="shrink-0" data-testid={`badge-sop-sharing-${sop.id}`}>
      <Users className="w-3 h-3 mr-1" />
      {count} selected assistant{count === 1 ? "" : "s"}
    </Badge>
  );
  if (names.length === 0) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span onClick={(e) => e.stopPropagation()}>{badge}</span>
      </TooltipTrigger>
      <TooltipContent data-testid={`tooltip-sop-sharing-${sop.id}`}>
        <div className="max-w-xs">
          <p className="font-medium mb-1">Shared with:</p>
          <ul className="space-y-0.5">
            {names.map((name, i) => (
              <li key={i}>{name}</li>
            ))}
          </ul>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

export default function AdminSopsPage() {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingSopId, setEditingSopId] = useState<string | null>(null);

  const { data: sops = [], isLoading } = useQuery<SOP[]>({
    queryKey: ["/api/sops", search, category, status],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (category !== "all") params.append("category", category);
      if (status !== "all") params.append("status", status);
      const res = await fetch(`/api/sops?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch SOPs");
      return res.json();
    }
  });

  const { data: categories = [] } = useQuery<{ id: string; name: string; color: string }[]>({
    queryKey: ["/api/categories", "sops"],
    queryFn: async () => {
      const res = await fetch("/api/categories?scope=sops");
      if (!res.ok) throw new Error("Failed to fetch categories");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/sops/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete SOP");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sops"] });
      queryClient.invalidateQueries({ queryKey: ["/api/sops/categories"] });
      toast({ title: "SOP deleted successfully" });
    },
    onError: () => {
      toast({ title: "Failed to delete SOP", variant: "destructive" });
    }
  });

  const handleCreate = () => {
    setEditingSopId(null);
    setIsEditorOpen(true);
  };

  const handleEdit = (id: string) => {
    setEditingSopId(id);
    setIsEditorOpen(true);
  };

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm("Are you sure you want to delete this SOP?")) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="w-full min-h-screen p-4 md:p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Manage SOPs</h1>
          <p className="text-muted-foreground">Standard Operating Procedures for humans and AI</p>
        </div>
        <Button onClick={handleCreate} data-testid="button-create-sop">
          <Plus className="w-4 h-4 mr-2" />
          Create SOP
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search SOPs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="input-search-sops"
          />
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-full md:w-[180px]" data-testid="select-filter-category">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.name}>
                  <span className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                    {c.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-full md:w-[150px]" data-testid="select-filter-status">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border-y" data-testid="list-admin-sops">
        <div className="hidden md:grid grid-cols-[minmax(0,2fr)_minmax(8rem,1fr)_8rem_9rem_3rem] gap-4 px-4 py-2.5 bg-muted/50 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <span>Name</span>
          <span>Category</span>
          <span>Audience</span>
          <span>Updated</span>
          <span className="sr-only">Actions</span>
        </div>
        {isLoading ? (
          <div className="divide-y">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-muted/30 animate-pulse" />
            ))}
          </div>
        ) : sops.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No SOPs found matching your filters.
          </div>
        ) : (
          <div className="divide-y">
            {sops.map((sop) => (
              <div
                key={sop.id}
                className="group grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,2fr)_minmax(8rem,1fr)_8rem_9rem_3rem] items-center gap-x-4 gap-y-2 px-4 py-3 hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => handleEdit(sop.id)}
                data-testid={`row-admin-sop-${sop.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{sop.title}</span>
                    <Badge
                      variant="outline"
                      className={sop.status === "published"
                        ? "text-green-600 border-green-600"
                        : "text-yellow-600 border-yellow-600"}
                    >
                      {sop.status === "published" ? "Published" : "Draft"}
                    </Badge>
                    <SharingBadge sop={sop} />
                  </div>
                  <div className="md:hidden mt-1 text-sm text-muted-foreground">
                    {[sop.category, sop.audience, format(new Date(sop.updatedAt), "MMM d, yyyy")]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
                <span className="hidden md:block text-sm text-muted-foreground truncate">
                  {sop.category || "—"}
                </span>
                <span className="hidden md:block text-sm text-muted-foreground capitalize">
                  {sop.audience}
                </span>
                <span className="hidden md:block text-sm text-muted-foreground">
                  {format(new Date(sop.updatedAt), "MMM d, yyyy")}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={(e) => handleDelete(sop.id, e)}
                  disabled={deleteMutation.isPending}
                  aria-label={`Delete ${sop.title}`}
                  data-testid={`button-delete-sop-${sop.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <SopEditorDialog
        open={isEditorOpen}
        onOpenChange={setIsEditorOpen}
        sopId={editingSopId}
      />
    </div>
  );
}
